<script setup>
import ImportEmailingExclusionsAction from '@frontJs/components/actions/projectManagement/modules/ImportEmailingExclusionsAction.vue';
import TrayArrowDownIcon from '@iconify-vue/ph/cloud-arrow-down';
import TrashIcon from '@iconify-vue/ph/trash';
import DeleteAction from '@js/components/actions/DeleteAction.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import ExportButton from '@js/components/buttons/ExportButton.vue';
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import BaseCell from '@js/components/tables/cells/BaseCell.vue';
import HeaderCell from '@js/components/tables/cells/HeaderCell.vue';
import PillCell from '@js/components/tables/cells/PillCell.vue';
import TextCell from '@js/components/tables/cells/TextCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import { useRoute } from '@js/utils/route';
import { toastDeleted } from '@js/utils/toast';
import { createColumnHelper } from '@tanstack/vue-table';
import { h } from 'vue';

const { tenant, emailing } = defineProps({
    tenant: {
        type: Object,
        required: true,
    },
    emailing: {
        type: Object,
        required: false,
    },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
    loading,
    table,
    count,
    globalFilter,
    filters,
    availableFilters,
    setGlobalFilter,
    setFilters,
    updateData,
    selectionIds,
    exportData,
} = useDataTable({
    columns: [
        columnHelper.accessor(() => null, {
            id: 'value',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Valeur',
                }),
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.value,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'type',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Type',
                }),
            cell: (props) =>
                h(PillCell, {
                    pillValue: props.row.original.type.label,
                }),
        }),
        columnHelper.display({
            id: 'actions',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Actions',
                    width: 0,
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () =>
                            h(
                                DeleteAction,
                                {
                                    url: route(
                                        'front.projectManagement.modules.deleteEmailingExclusion',
                                        {
                                            tenant: tenant.id,
                                            emailingExclusion:
                                                props.row.original.id,
                                        },
                                    ),
                                    callback: () => {
                                        toastDeleted();

                                        updateData();
                                    },
                                },
                                {
                                    trigger: () =>
                                        h(ActionButton, {
                                            type: 'secondary',
                                            icon: TrashIcon,
                                            size: 'small',
                                            title: 'Supprimer',
                                            square: true,
                                        }),
                                },
                            ),
                    },
                ),
        }),
    ],
    url: route('front.projectManagement.modules.postEmailingExclusions', {
        tenant: tenant.id,
    }),
    exportUrl: route(
        'front.projectManagement.modules.postExportEmailingExclusions',
        {
            tenant: tenant.id,
        },
    ),
    data: {
        emailingId: emailing ? emailing.id : null,
    },
    columnPinning: {
        right: ['actions'],
    },
    selectionIdKey: 'id',
    exportSelectionIdKey: 'id',
});
</script>

<template>
    <DataTableLayout
        :table="table"
        :count="count"
        :global-filter="globalFilter"
        :filters="filters"
        :available-filters="availableFilters"
        :set-filters="setFilters"
        :set-global-filter="setGlobalFilter"
        :loading="loading"
    >
        <template #actions>
            <ExportButton
                :csv-export-callback="() => exportData('csv')"
                :xlsx-export-callback="() => exportData('xlsx')"
                :disabled="selectionIds.length === 0"
            />

            <ImportEmailingExclusionsAction
                :tenant="tenant"
                :emailing="emailing"
                :callback="updateData"
            >
                <template #trigger>
                    <ActionButton
                        size="small"
                        :left-icon="TrayArrowDownIcon"
                        type="primary"
                    >
                        Importer
                    </ActionButton>
                </template>
            </ImportEmailingExclusionsAction>
        </template>
    </DataTableLayout>
</template>
